import { useCallback, useEffect, useMemo, useState } from 'react'
import { STORAGE_KEYS, validateApiKey, normalizeStoredKey } from '../activity-stream/apiKeyUtils.js'

const readStoredKeys = () => {
  try {
    const raw = localStorage.getItem(STORAGE_KEYS.API_KEYS)
    if (!raw) {
      const legacy = localStorage.getItem(STORAGE_KEYS.API_KEY)
      if (legacy) {
        const migrated = [
          normalizeStoredKey({
            id: typeof crypto !== 'undefined' && crypto.randomUUID ? crypto.randomUUID() : `key-${Date.now()}`,
            label: 'Default',
            apiKey: legacy,
            lastUsed: new Date().toISOString(),
          }),
        ]
        localStorage.setItem(STORAGE_KEYS.API_KEYS, JSON.stringify(migrated))
        localStorage.removeItem(STORAGE_KEYS.API_KEY)
        return migrated
      }
      return []
    }
    const parsed = JSON.parse(raw)
    if (!Array.isArray(parsed)) {
      return []
    }
    return parsed.map(normalizeStoredKey)
  } catch (error) {
    console.warn('Failed to load stored API keys', error)
    return []
  }
}

export const useStoredApiKeys = () => {
  const [keys, setKeys] = useState(() => readStoredKeys())
  const [activeKeyId, setActiveKeyId] = useState(() => {
    try {
      return localStorage.getItem(STORAGE_KEYS.ACTIVE_KEY_ID) || null
    } catch (_error) {
      return null
    }
  })

  const activeKey = useMemo(
    () => keys.find((entry) => entry.id === activeKeyId) || null,
    [keys, activeKeyId],
  )

  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEYS.API_KEYS, JSON.stringify(keys))
    } catch (error) {
      console.warn('Failed to persist stored API keys', error)
    }
  }, [keys])

  useEffect(() => {
    try {
      if (activeKeyId) {
        localStorage.setItem(STORAGE_KEYS.ACTIVE_KEY_ID, activeKeyId)
      } else {
        localStorage.removeItem(STORAGE_KEYS.ACTIVE_KEY_ID)
      }
    } catch (error) {
      console.warn('Failed to persist active API key', error)
    }
  }, [activeKeyId])

  useEffect(() => {
    if (typeof window === 'undefined') {
      return
    }
    const detail = { apiKey: activeKey?.apiKey ?? null }
    window.dispatchEvent(
      new CustomEvent('activity-stream:active-key-changed', { detail }),
    )
  }, [activeKey?.apiKey])

  const addKey = useCallback(
    ({ label, apiKey, writeKey }) => {
      const validation = validateApiKey(apiKey)
      if (!validation.valid) {
        return { success: false, error: validation.message }
      }
      const entry = normalizeStoredKey({
        id: typeof crypto !== 'undefined' && crypto.randomUUID ? crypto.randomUUID() : `key-${Date.now()}`,
        label: label?.trim() || 'Untitled',
        apiKey: apiKey.trim(),
        writeKey: writeKey?.trim() || '',
        lastUsed: new Date().toISOString(),
      })
      setKeys((previous) => [...previous, entry])
      setActiveKeyId(entry.id)
      return { success: true, key: entry }
    },
    [],
  )

  const updateKey = useCallback((id, updates) => {
    if (updates.apiKey) {
      const validation = validateApiKey(updates.apiKey)
      if (!validation.valid) {
        return { success: false, error: validation.message }
      }
    }
    setKeys((previous) =>
      previous.map((entry) =>
        entry.id === id
          ? {
              ...entry,
              ...updates,
              apiKey: updates.apiKey !== undefined ? updates.apiKey.trim() : entry.apiKey,
              writeKey: updates.writeKey !== undefined ? updates.writeKey.trim() : entry.writeKey,
            }
          : entry,
      ),
    )
    return { success: true }
  }, [])

  const deleteKey = useCallback((id) => {
    setKeys((previous) => {
      const filtered = previous.filter((entry) => entry.id !== id)
      if (activeKeyId === id) {
        setActiveKeyId(filtered[0]?.id ?? null)
      }
      return filtered
    })
    return { success: true }
  }, [activeKeyId])

  const touchKey = useCallback((id) => {
    setKeys((previous) =>
      previous.map((entry) =>
        entry.id === id ? { ...entry, lastUsed: new Date().toISOString() } : entry,
      ),
    )
  }, [])

  const updateMetadata = useCallback((id, partition, region) => {
    setKeys((previous) =>
      previous.map((entry) =>
        entry.id === id ? { ...entry, partition, region } : entry,
      ),
    )
  }, [])

  const clearActiveSelection = useCallback(() => {
    setActiveKeyId(null)
  }, [])

  return {
    keys,
    activeKey,
    activeKeyId,
    setActiveKeyId,
    addKey,
    updateKey,
    deleteKey,
    touchKey,
    updateMetadata,
    clearActiveSelection,
  }
}

export default useStoredApiKeys
